// src/components/Splash/Splash.js
import React, { useEffect, useState } from "react";
import "./Splash.css";
import ACMLogo from "../Logo/DIET_ACM_Logo.png";

const MINIMUM_DISPLAY_TIME = 1500; // 1.5 seconds smooth aesthetic experience

const Splash = ({ children }) => {
  const [isSplashLoading, setIsSplashLoading] = useState(true);
  const [isFading, setIsFading] = useState(false);
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    const startTime = Date.now();
    let loadedCount = 0;
    let totalCount = 0;

    const images = document.querySelectorAll("img");
    const fonts = document.fonts ? Array.from(document.fonts) : [];
    totalCount = Math.max(1, images.length + fonts.length);

    const updateProgress = () => {
      const elapsed = Date.now() - startTime;
      const assetPercent = (loadedCount / totalCount) * 80;
      const timePercent = Math.min((elapsed / MINIMUM_DISPLAY_TIME) * 100, 100);
      
      const currentProgress = Math.min(Math.round(Math.max(assetPercent, timePercent)), 100);
      setProgress((prev) => Math.max(prev, currentProgress));

      if (elapsed >= MINIMUM_DISPLAY_TIME && currentProgress >= 100) {
        completeLoading();
      }
    };

    const completeLoading = () => {
      setProgress(100);
      setTimeout(() => {
        setIsFading(true);
        setTimeout(() => {
          setIsSplashLoading(false);
        }, 500);
      }, 250);
    };

    // Track images
    images.forEach((img) => {
      if (img.complete) {
        loadedCount++;
      } else {
        img.addEventListener("load", () => {
          loadedCount++;
          updateProgress();
        });
        img.addEventListener("error", () => {
          loadedCount++;
          updateProgress();
        });
      }
    });

    // Track fonts
    if (document.fonts) {
      fonts.forEach((font) => {
        font.loaded
          .then(() => {
            loadedCount++;
            updateProgress();
          })
          .catch(() => {
            loadedCount++;
            updateProgress();
          });
      });
    }

    // Interval to guarantee smooth ticking progress
    const ticker = setInterval(() => {
      const elapsed = Date.now() - startTime;
      const timePercent = Math.min(Math.round((elapsed / MINIMUM_DISPLAY_TIME) * 100), 100);
      setProgress((prev) => Math.max(prev, timePercent));

      if (elapsed >= MINIMUM_DISPLAY_TIME) {
        clearInterval(ticker);
        completeLoading();
      }
    }, 50);

    return () => clearInterval(ticker);
  }, []);

  const getStatusText = (prog) => {
    if (prog < 30) return "Authenticating Security Environment...";
    if (prog < 65) return "Loading Chapter Control Systems...";
    if (prog < 95) return "Syncing Database Schemas & Endpoints...";
    return "Welcome to DIET ACM Admin Console!";
  };

  return (
    <>
      {isSplashLoading ? (
        <div className={`splash-overlay ${isFading ? "fade-out" : ""}`}>
          {/* Ambient Background Lights & Grid */}
          <div className="splash-ambient-glow top-right"></div>
          <div className="splash-ambient-glow bottom-left"></div>
          <div className="splash-grid-bg"></div>

          <div className="splash-container">
            {/* Glassmorphic Logo Showcase Card */}
            <div className="splash-card">
              <div className="splash-logo-container">
                <div className="splash-glow-ring ring-outer"></div>
                <div className="splash-glow-ring ring-inner"></div>
                <img
                  src={ACMLogo}
                  alt="DIET ACM Admin Console"
                  className="splash-logo"
                />
              </div>

              <div className="splash-badge">
                <span className="badge-sparkle">✦</span>
                <span className="badge-text">DIET ACM ADMIN CONSOLE</span>
                <span className="badge-sparkle">✦</span>
              </div>

              <h2 className="splash-subtitle">Chapter Command &amp; Management Portal</h2>

              {/* High-Tech Progress Bar */}
              <div className="splash-progress-wrapper">
                <div className="splash-progress-track">
                  <div
                    className="splash-progress-bar"
                    style={{ width: `${progress}%` }}
                  >
                    <div className="splash-progress-glint"></div>
                    <div className="splash-progress-head"></div>
                  </div>
                </div>

                <div className="splash-status-row">
                  <span className="splash-status-text">
                    <span className="status-indicator-dot"></span>
                    {getStatusText(progress)}
                  </span>
                  <span className="splash-progress-number">{progress}%</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      ) : (
        children
      )}
    </>
  );
};

export default Splash;
